<template>
    <div class="dropdown-container" ref="target">
      <button type="button" @click="showDropdown = !showDropdown" class="dropdown-button">
        <p>
          <span v-if="selectedOption">{{ displayText(selectedOption, optionAttribute) }}</span>
          <span v-else>Select {{ label }}</span>
        </p>
  
        <i class="fa fa-angle-down icon-chevron"></i>
      </button>
  
      <ul v-if="showDropdown" class="dropdown-list">
        <input
          v-model="searchInput"
          type="text"
          :placeholder="placeholder"
          @input="updateSearch"
          class="dropdown-search"
        />
  
        <div v-if="filteredOptions.length > 0">
          <li
            v-for="(option, index) in filteredOptions"
            :key="index"
            @click="selectOption(option)"
            class="dropdown-item"
          >
            <div class="option-name">{{ displayText(option, optionAttribute) }}</div>
          </li>
        </div>
        <div v-else>
          <li class="dropdown-no-options">
            <div class="option-name">
              <span v-if="searchInput">No results for '{{ searchInput }}'.</span>
              <span v-else>No Options.</span>
            </div>
          </li>
        </div>
      </ul>
    </div>
  </template>
  
  <script setup>
  import { ref, watch, computed, defineEmits, defineProps } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  
  const props = defineProps({
    options: {
      type: Array,
      required: true
    },
    modelValue: {
      type: [Object, String, null],
      default: null
    },
    label: {
      type: String,
      required: true
    },

    optionAttribute: {
      type: String,
      required: true
    },
    searchAttribute: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    }
  })
  
  const emit = defineEmits(['update:modelValue', 'search-updated', 'selected'])
  
  const target = ref(null)
  const showDropdown = ref(false)
  const selectedOption = ref(props.modelValue)
  const searchInput = ref('')
  
  onClickOutside(target, () => {
    showDropdown.value = false
  })
  
  watch(showDropdown, () => {
    searchInput.value = ''
    updateSearch()
  })
  
  function updateSearch() {
    emit('search-updated', searchInput.value)
  }
  
  function displayText(option, attr) {
    if (typeof option === 'string') return option
    if (typeof option === 'object' && option !== null) {
      return option[attr] || ''
    }
    return ''
  }
  
  const filteredOptions = computed(() => {
    if (!searchInput.value) return props.options
    const searchLower = searchInput.value.toLowerCase()
    return props.options.filter(option => {
      const text = displayText(option, props.searchAttribute)
      return text.toLowerCase().includes(searchLower)
    })
  })
  
  function selectOption(option) {
    selectedOption.value = option
    emit('update:modelValue', option)
    emit('selected', option)
    showDropdown.value = false
  }
  watch(() => props.modelValue, (newVal) => {
  selectedOption.value = newVal
})
  </script>
  
  
  <style scoped>
  .dropdown-container {
    position: relative;
  }
  
  .dropdown-button {
    position: relative;
    width: 100%;
    cursor: default;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding: 10px 5px 10px 5px;
  font-size: 14px;
    border: 1px solid #e5e5e5;
  border-radius: 6px;
    border-radius: 6px;
    background-color: #ffffff;
    color: #8f9bba;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    outline: none;
  }
  
  .dropdown-button:focus {
    outline: 1px solid #3865b0;
  }
  
  .icon-chevron {
    width: 16px;
    height: 16px;
  }
  
  .dropdown-list {
    position: absolute;
    width: 100%;
    z-index: 20;
    margin-top: 8px;
    background-color: #ffffff;
    color: #111827;
    border-radius: 6px;
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
    max-height: 192px;
    overflow-y: auto;
    padding: 4px;
    border: 1px solid #e5e7eb;
  }
  
  .dropdown-search {
    width: calc(100% + 8px);
    padding: 6px 12px;
    font-size: 14px;
    background-color: #ffffff;
    color: #374151;
    border: none;
    border-bottom: 1px solid #e5e7eb;
    margin: -4px -4px 4px;
    position: sticky;
    top: -4px;
    z-index: 10;
  }
  
  .dropdown-search::placeholder {
    color: #9ca3af;
  }
  
  .dropdown-item {
    cursor: default;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px;
    font-size: 14px;
    border-radius: 6px;
  }
  
  .dropdown-item:hover {
    background-color: #f3f4f6;
  }
  
  .option-name {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
  
  .dropdown-no-options {
    color: #9ca3af;
    font-size: 14px;
    padding: 6px;
    user-select: none;
  }
  </style>
  